import {Fragment} from "react";
import {defineMessage, FormattedMessage, MessageDescriptor} from "react-intl";
import {DateTime, Duration} from "luxon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ClickAwayListener, IconButton, Tooltip} from "@mui/material";
import {useBoolState} from "@/hooks";

type Props = {
    registrationTime?: string;
    expirationTime?: string;
    clientInfo: string;
};

export default function FormattedRegistrationTime({registrationTime, expirationTime, clientInfo}: Props) {
    if (!registrationTime)
        return (
            <span style={{color: "red"}}>
                <FormattedMessage id="never" defaultMessage="Never" />
            </span>
        );

    const [isOpen, open, close] = useBoolState();
    const registered = DateTime.fromISO(registrationTime);
    const expire = expirationTime ? DateTime.fromISO(expirationTime) : null;

    const now = DateTime.now();
    const elapsedTimeSinceRegistration = now.diff(registered);

    const message = new Message(elapsedTimeSinceRegistration);

    const tooltipTitle = (
        <Fragment>
            <p style={{fontSize: "0.75rem"}}>
                <FormattedMessage
                    id="registration_time"
                    defaultMessage="Registered at: {time}"
                    values={{time: registered.toLocaleString(DateTime.DATETIME_SHORT)}}
                />
            </p>
            <p style={{fontSize: "0.75rem"}}>
                {expire ? (
                    <FormattedMessage
                        id="expiration_time"
                        defaultMessage="Expires at: {time}"
                        values={{time: expire.toLocaleString(DateTime.DATETIME_SHORT)}}
                    />
                ) : (
                    <FormattedMessage
                        id="expiration_time"
                        defaultMessage="Expires at: {time}"
                        values={{time: <FormattedMessage id="not_set" defaultMessage="Not set" />}}
                    />
                )}
            </p>
            <p style={{fontSize: "0.75rem"}}>
                <FormattedMessage id="client_info" defaultMessage="Client info: {value}" values={{value: clientInfo}} />
            </p>
        </Fragment>
    );

    const registeredProps = expire && expire < now ? {style: {color: "red"}} : {};

    return (
        <span>
            <span {...registeredProps}>
                <FormattedMessage {...message.id} values={message.values} />
            </span>
            <ClickAwayListener onClickAway={close}>
                <Tooltip
                    PopperProps={{disablePortal: true}}
                    onClose={close}
                    open={isOpen}
                    title={tooltipTitle}
                    placement="top"
                    arrow
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                >
                    <span style={{paddingLeft: 5}}>
                        <IconButton onClick={open} size="small" color="primary">
                            <FontAwesomeIcon icon="info-circle" />
                        </IconButton>
                    </span>
                </Tooltip>
            </ClickAwayListener>
        </span>
    );
}

type MessageValue = {
    time?: number;
};

type DimensionId = {
    minute: MessageDescriptor;
    hour: MessageDescriptor;
    day: MessageDescriptor;
};

type MessageMapType = {
    singular: DimensionId;
    plural: DimensionId;
};

const messageMap: MessageMapType = {
    singular: {
        minute: defineMessage({id: "1_minute_ago", defaultMessage: "1 minute ago"}),
        hour: defineMessage({id: "1_hour_ago", defaultMessage: "1 hour ago"}),
        day: defineMessage({id: "1_day_ago", defaultMessage: "1 day ago"}),
    },
    plural: {
        minute: defineMessage({id: "x_minutes_ago", defaultMessage: "{time} minutes ago"}),
        hour: defineMessage({id: "x_hours_ago", defaultMessage: "{time} hours ago"}),
        day: defineMessage({id: "x_days_ago", defaultMessage: "{time} days ago"}),
    },
};

abstract class BaseMessage {
    id: MessageDescriptor;
    values: MessageValue;
}

class Message extends BaseMessage {
    constructor(duration: Duration) {
        super();
        let elapsedMinutes = Math.floor(duration.as("minutes"));

        if (elapsedMinutes === 0 && duration.as("minutes") > 0) elapsedMinutes = 1;

        const elapsedHours = Math.floor(duration.as("hours"));
        const elapsedDays = Math.floor(duration.as("days"));

        const base = elapsedDays
            ? this.getBase(elapsedDays, "day")
            : elapsedHours
              ? this.getBase(elapsedHours, "hour")
              : this.getBase(elapsedMinutes, "minute");

        this.id = base.id;
        this.values = {...base.values};
    }

    protected getBase(time: number, dimensionKey: keyof DimensionId): BaseMessage {
        const mode = time > 1 ? "plural" : "singular";
        const id = messageMap[mode][dimensionKey];

        return {id, values: {time}};
    }
}
